/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, PhoneOff, MicOff, Volume2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import type { FakeCallPreset } from "@/types/safecircle";
import { FAKE_CALL_EVENT, type FakeCallEventDetail } from "./fakeCallEvents";

type State = "idle" | "scheduled" | "ringing" | "in_call" | "ended";

export default function FakeCallScreen() {
  const { t } = useTranslation();
  const [preset, setPreset] = useState<FakeCallPreset | null>(null);
  const [state, setState] = useState<State>("idle");
  const [callDuration, setCallDuration] = useState(0);
  const tripleTapCountRef = useRef(0);
  const tripleTapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const onTrigger = (ev: Event) => {
      const detail = (ev as CustomEvent<FakeCallEventDetail>).detail;
      if (!detail?.preset) return;
      clearTimers();
      setPreset(detail.preset);
      setState("scheduled");
      if (detail.preset.delaySeconds > 0) {
        scheduleTimerRef.current = setTimeout(
          () => setState("ringing"),
          detail.preset.delaySeconds * 1000,
        );
      } else {
        setState("ringing");
      }
    };
    window.addEventListener(FAKE_CALL_EVENT, onTrigger);
    return () => {
      window.removeEventListener(FAKE_CALL_EVENT, onTrigger);
      clearTimers();
    };
  }, []);

  useEffect(() => {
    if (state !== "ringing" || !Capacitor.isNativePlatform()) return;
    const id = setInterval(() => {
      Haptics.impact({ style: ImpactStyle.Medium }).catch(() => {});
    }, 1500);
    return () => clearInterval(id);
  }, [state]);

  useEffect(() => {
    if (state !== "in_call") return;
    setCallDuration(0);
    callTimerRef.current = setInterval(() => {
      setCallDuration((s) => s + 1);
    }, 1000);
    return () => {
      if (callTimerRef.current) clearInterval(callTimerRef.current);
    };
  }, [state]);

  const clearTimers = () => {
    if (scheduleTimerRef.current) clearTimeout(scheduleTimerRef.current);
    if (callTimerRef.current) clearInterval(callTimerRef.current);
    if (tripleTapTimerRef.current) clearTimeout(tripleTapTimerRef.current);
  };

  const handleAccept = () => setState("in_call");
  const handleDecline = () => {
    clearTimers();
    setState("ended");
    setTimeout(() => {
      setPreset(null);
      setState("idle");
    }, 400);
  };

  const handleScreenTap = () => {
    tripleTapCountRef.current += 1;
    if (tripleTapTimerRef.current) {
      clearTimeout(tripleTapTimerRef.current);
    }
    tripleTapTimerRef.current = setTimeout(() => {
      tripleTapCountRef.current = 0;
    }, 800);

    if (tripleTapCountRef.current >= 3) {
      tripleTapCountRef.current = 0;
      // Trigger a real SOS event via window event that SOSButton (or another
      // listener) can pick up.
      window.dispatchEvent(new CustomEvent("safeCircle:realSOS"));
    }
  };

  if (state === "idle" || !preset) return null;

  const avatarColor = preset.callerAvatarColor ?? "#64748b";
  const mmss = `${Math.floor(callDuration / 60)
    .toString()
    .padStart(2, "0")}:${(callDuration % 60).toString().padStart(2, "0")}`;

  return (
    <AnimatePresence>
      {(state === "ringing" || state === "in_call" || state === "scheduled") && (
        <motion.div
          key="fake-call-screen"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleScreenTap}
          className="fixed inset-0 z-[100] bg-gradient-to-b from-slate-900 to-black text-white flex flex-col items-center justify-between py-16"
        >
          {state === "scheduled" && (
            <div className="flex-1 flex items-center justify-center text-xs text-muted-foreground">
              {t("safeCircle.fakeCall.scheduled")}
            </div>
          )}

          {(state === "ringing" || state === "in_call") && (
            <>
              <div className="flex-1 flex flex-col items-center justify-center gap-4">
                <p className="text-sm text-slate-400">
                  {state === "ringing"
                    ? t("safeCircle.fakeCall.incomingCall")
                    : t("safeCircle.fakeCall.inCall", { time: mmss })}
                </p>
                <div
                  className="w-28 h-28 rounded-full flex items-center justify-center text-4xl font-bold"
                  style={{
                    backgroundColor: avatarColor + "40",
                    color: "#fff",
                  }}
                >
                  {preset.callerName.charAt(0).toUpperCase()}
                </div>
                <h1 className="text-3xl font-semibold">{preset.callerName}</h1>
                {state === "ringing" && (
                  <p className="text-slate-400 text-sm animate-pulse">
                    {t("safeCircle.fakeCall.ringing")}
                  </p>
                )}
                {state === "in_call" && preset.script && (
                  <p className="text-slate-300 text-xs max-w-xs text-center mt-4 italic">
                    "{preset.script}"
                  </p>
                )}
              </div>

              {state === "ringing" ? (
                <div className="flex items-center justify-around w-full px-8">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDecline();
                    }}
                    className="w-16 h-16 rounded-full bg-red-600 text-white flex items-center justify-center shadow-lg"
                    aria-label={t("safeCircle.fakeCall.decline")}
                  >
                    <PhoneOff size={28} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAccept();
                    }}
                    className="w-16 h-16 rounded-full bg-emerald-600 text-white flex items-center justify-center shadow-lg animate-pulse"
                    aria-label={t("safeCircle.fakeCall.accept")}
                  >
                    <Phone size={28} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-around w-full px-8">
                  <div className="flex flex-col items-center gap-1 text-slate-400 text-xs">
                    <div className="w-12 h-12 rounded-full bg-slate-700/50 flex items-center justify-center">
                      <MicOff size={18} />
                    </div>
                    {t("safeCircle.fakeCall.mute")}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDecline();
                    }}
                    className="w-16 h-16 rounded-full bg-red-600 text-white flex items-center justify-center shadow-lg"
                    aria-label={t("safeCircle.fakeCall.hangUp")}
                  >
                    <PhoneOff size={28} />
                  </button>
                  <div className="flex flex-col items-center gap-1 text-slate-400 text-xs">
                    <div className="w-12 h-12 rounded-full bg-slate-700/50 flex items-center justify-center">
                      <Volume2 size={18} />
                    </div>
                    {t("safeCircle.fakeCall.speaker")}
                  </div>
                </div>
              )}

              <p className="mt-6 text-[11px] text-slate-400 px-6 text-center">
                {t("safeCircle.fakeCall.tripleTapHint")}
              </p>
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
